import { Tooltip } from "@material-ui/core";
import moment from "moment-timezone";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { DeviceType } from "../../redux/devices";
import {
  ActivityStatus,
  PostureStatusValue,
  UserNearbyStatus,
} from "../../redux/devicesStatuses";
import {
  activityStatusCreator,
  bluetoothStatusCreator,
  occupancyStatusCreator,
  onOffStatusCreator,
  postrureStatusCreator,
  powerStatusCreator,
  residentActiveBasedonDevicesCreator,
  userNearbyStatusCreator,
} from "../../redux/devicesStatuses/selectors";
import {
  DeviceMinimizedMap,
  presenceStatuses,
  Resident,
} from "../../redux/residents";
import {
  residentDevicesAfterGateCreator,
  residentsCreator,
} from "../../redux/residents/selectors";
import { BedStatusValue } from "../../redux/residentStatuses";
import { bedStatusCreator } from "../../redux/residentStatuses/selectors";
import { IAppState } from "../../redux/types";
import { DateHelper } from "../../utils/DateHelper";
import { UserTagOrMobileDeviceAsTagGate } from "../../utils/featureFlags/FeatureGates";
import { langByLocal, Language } from "../../utils/Languages";
import { capitalize } from "../common/Common";
import {
  BadBiopatchStatus,
  BadWatchStatus,
  DeviceStatePower,
  MarkForWifi,
  nearbyBack,
  nearbyBelly,
  nearbyInGG,
  nearbyInWG,
  nearbyLeft,
  nearbyOutGG,
  nearbyOutWG,
  nearbyRight,
  nearbyRoomWG,
  nearbySit,
  nearbyStand,
} from "./devices";
import { ViewType, PresentationType } from "../../redux/alerts";

export const TextForDevice = React.memo(
  ({
    residentId,
    deviceType,
    deviceId,
  }: {
    residentId: string;
    deviceType: DeviceType;
    deviceId: string;
  }): JSX.Element => {
    const selection = useMemo(() => activityStatusCreator(), []);
    const status = useSelector((state: IAppState) =>
      selection(state, deviceId)
    );
    // if (!status) debugger;
    if (status?.isActive)
      return (
        <IfDeviceActive
          residentId={residentId}
          deviceType={deviceType}
          deviceId={deviceId}
        />
      );
    return <IfDeviceInActive deviceId={deviceId} deviceType={deviceType} />;
  }
);
TextForDevice.displayName = "TextForDevice";
export const IfDeviceInActive = React.memo(
  ({
    deviceId,
    deviceType,
  }: {
    deviceId: string;
    deviceType: DeviceType;
  }): JSX.Element => {
    const onoff = useMemo(() => onOffStatusCreator(), []);
    const onOffStatus = useSelector((state: IAppState) =>
      onoff(state, deviceId)
    );
    const { t } = useTranslation("common");
    if (
      onOffStatus &&
      deviceType === DeviceType.vitals &&
      onOffStatus?.isOn === false
    )
      return <>{t("labels.off")}</>;
    return deviceType === DeviceType.vitals ? (
      <IfVitalsInActive deviceId={deviceId} />
    ) : (
      <IfBiopatchInActive deviceId={deviceId} />
    );
  }
);
IfDeviceInActive.displayName = "IfDeviceInActive";

export const IfVitalsInActive = React.memo(
  ({ deviceId }: { deviceId: string }): JSX.Element => {
    const badStatusMemo = useMemo(() => activityStatusCreator(), []);
    const badStatus = useSelector((state: IAppState) =>
      badStatusMemo(state, deviceId)
    );

    const { t } = useTranslation("monitor");
    const { language } = useSelector((state: IAppState) => state.userProfile);
    const lang = langByLocal(language);

    const whosDate = badStatus?.datetime;
    const time = whosDate ? findTimetoShow(whosDate, lang) : t("unknownTime");

    return <>{t("vitOffExpl", { timeConnected: time })}</>;
  }
);
IfVitalsInActive.displayName = "IfVitalsInActive";
export const findTimetoShow = (whosDate: string, lang: Language): string => {
  const res = DateHelper.GetDateAndTime(whosDate).date;

  const now = moment.now();

  if (moment(now).dayOfYear() === moment(whosDate).dayOfYear())
    return DateHelper.LocalTime(whosDate, "LT");
  const yearPart = moment(whosDate).format("YYYY");
  const isBefore = lang.code === "ja" || lang.code.indexOf("zh") !== -1;
  const LocalSeparator =
    res.indexOf("/") > -1 ? "/" : res.indexOf("-") > -1 ? "-" : ".";
  const start = res.replace(
    !isBefore ? LocalSeparator + yearPart : yearPart + LocalSeparator,
    ""
  );

  return start + " " + DateHelper.LocalTime(whosDate, "LT");
};

export const IfBiopatchInActive = React.memo(
  ({ deviceId }: { deviceId: string }): JSX.Element => {
    const selectionBlue = useMemo(() => bluetoothStatusCreator(), []);
    const statusBlue = useSelector((state: IAppState) =>
      selectionBlue(state, deviceId)
    );

    const selectionBattery = useMemo(() => powerStatusCreator(), []);
    const statusPower = useSelector((state: IAppState) =>
      selectionBattery(state, deviceId)
    );
    const { t } = useTranslation("status");
    const { language } = useSelector((state: IAppState) => state.userProfile);
    const lang = langByLocal(language);

    const whosDate =
      statusPower?.batteryLevel === 0 && !statusPower?.connected
        ? statusPower?.datetime
        : statusBlue?.datetime;

    const time = whosDate ? findTimetoShow(whosDate, lang) : t("unknownTime");

    return (
      <>
        <span>{t("vitOffExpl", { timeConnected: time })}</span>
      </>
    );
  }
);
IfBiopatchInActive.displayName = "IfBiopatchInActive";

export const IfDeviceActive = React.memo(
  ({
    residentId,
    deviceType,
    deviceId,
  }: {
    residentId: string;
    deviceType: DeviceType;
    deviceId: string;
  }): JSX.Element => {
    const { t } = useTranslation("common");
    const selection = useMemo(() => bedStatusCreator(), []);
    const status = useSelector((state: IAppState) =>
      selection(state, residentId)
    );
    const posture = useMemo(() => postrureStatusCreator(), []);
    const postureStatus = useSelector((state: IAppState) =>
      posture(state, deviceId)
    );
    const onoff = useMemo(() => onOffStatusCreator(), []);
    const onOffStatus = useSelector((state: IAppState) =>
      onoff(state, deviceId)
    );
    if (onOffStatus && onOffStatus?.isOn === false)
      return <>{t("labels.off")}</>;
    if (!status) return null;
    if (
      !postureStatus ||
      postureStatus?.value === PostureStatusValue.UNKNOWN ||
      deviceType === DeviceType.vitals
    ) {
      switch (status?.value?.toLowerCase()) {
        case BedStatusValue.IN_BED.toLowerCase():
          return <>{capitalize(t("labels.inBed"))}</>;
        case BedStatusValue.OUT_OF_BED.toLowerCase():
          return <>{capitalize(t("labels.outBed"))}</>;
        default:
          return null;
      }
    } else
      switch (postureStatus?.value?.toLowerCase()) {
        case PostureStatusValue.BACK.toLowerCase(): {
          return <>{capitalize(t("status:inBedBack"))}</>;
        }
        case PostureStatusValue.LEFT.toLowerCase(): {
          return <>{capitalize(t("status:inBedLeft"))}</>;
        }
        case PostureStatusValue.RIGHT.toLowerCase(): {
          return <>{capitalize(t("status:inBedRight"))}</>;
        }
        case PostureStatusValue.STOMACH.toLowerCase(): {
          return <>{capitalize(t("status:inBedBelly"))}</>;
        }
        case PostureStatusValue.STAND.toLowerCase(): {
          return <>{capitalize(t("status:outOfBedStand"))}</>;
        }
        case PostureStatusValue.WALK:
          return <>{capitalize(t("status:walk"))}</>;
        case PostureStatusValue.SIT:
          if (
            status.value.toLowerCase() === BedStatusValue.IN_BED.toLowerCase()
          )
            return <>{capitalize(t("status:inBedSit"))}</>;
          else return <>{capitalize(t("status:sitting"))}</>;
        case PostureStatusValue.FALL:
          return <>{capitalize(t("status:fall"))}</>;
        case PostureStatusValue.UNKNOWN.toLowerCase(): {
          return null;
        }
        case PostureStatusValue.UNATTACHED.toLowerCase(): {
          return <>{capitalize(t("status:notAttached"))}</>;
        }

        default:
          return null;
      }
  }
);
IfDeviceActive.displayName = "IfDeviceActive";

export const WatchIcons = React.memo(
  ({
    residentId,
    deviceId,
  }: {
    residentId: string;
    deviceId: string;
  }): JSX.Element => {
    const activity = useMemo(() => activityStatusCreator(), []);
    const activeStatus = useSelector((state: IAppState) =>
      activity(state, deviceId)
    );
    return (
      <>
        {activeStatus && activeStatus?.isActive && (
          <>
            <DeviceStatePower
              deviceId={deviceId}
              deviceType={DeviceType.watch}
            />
          </>
        )}

        {(!activeStatus || !activeStatus?.isActive) && (
          <BadWatchStatus deviceId={deviceId} />
        )}
      </>
    );
  }
);
WatchIcons.displayName = "WatchIcons";
export const BioPatchIcons = React.memo(
  ({
    residentId,
    deviceId,
  }: {
    residentId: string;
    deviceId: string;
  }): JSX.Element => {
    const posture = useMemo(() => postrureStatusCreator(), []);
    const postureStatus = useSelector((state: IAppState) =>
      posture(state, deviceId)
    );
    const bioActivity = useMemo(() => activityStatusCreator(), []);
    const bioActiveStatus = useSelector((state: IAppState) =>
      bioActivity(state, deviceId)
    );
    return (
      <>
        {bioActiveStatus && bioActiveStatus?.isActive && (
          <>
            {postureStatus?.value !== PostureStatusValue.UNATTACHED ? (
              <MarkForBed residentId={residentId} />
            ) : (
              <i className={"icon-not-attached white"} />
            )}
            <DeviceStatePower
              deviceId={deviceId}
              deviceType={DeviceType.biopatch}
            />
          </>
        )}

        {bioActiveStatus && !bioActiveStatus?.isActive && (
          <BadBiopatchStatus deviceId={deviceId} />
        )}
      </>
    );
  }
);
BioPatchIcons.displayName = "BioPatchIcons";
export const VitalsIcons = React.memo(
  ({
    residentId,
    deviceId,
    size,
  }: {
    residentId: string;
    deviceId: string;
    size?: number;
  }): JSX.Element => {
    const selection = useMemo(() => bedStatusCreator(), []);
    const status = useSelector((state: IAppState) =>
      selection(state, residentId)
    );
    const activity = useMemo(() => activityStatusCreator(), []);
    const activityStatus: ActivityStatus = useSelector((state: IAppState) =>
      activity(state, deviceId)
    );
    const onoff = useMemo(() => onOffStatusCreator(), []);
    const onoffStatus = useSelector((state: IAppState) =>
      onoff(state, deviceId)
    );
    const userNearby = useMemo(() => userNearbyStatusCreator(), []);
    const userNearbyStatus = useSelector((state: IAppState) =>
      userNearby(state, deviceId)
    );
    let icon = "";
    switch (status?.value?.toLowerCase()) {
      case BedStatusValue.IN_BED.toLowerCase():
        icon = `icon-alert-bed-in font-${size}`;
        break;

      case BedStatusValue.OUT_OF_BED.toLowerCase():
        icon = `icon-alert-bed-out font-${size} size-icon-${size}`;
        break;
    }
    if (!onoffStatus?.isOn) icon = "icon-off";
    const iconEl = activityStatus?.isActive ? (
      !onoffStatus?.isOn ? (
        <i className={"icon-off  white"} />
      ) : userNearbyStatus?.nearBed?.length > 0 ? (
        icon === `icon-alert-bed-in font-${size}` ? (
          nearbyInWG(0)
        ) : (
          nearbyOutWG(0)
        )
      ) : (
        <i className={icon + " white"} />
      )
    ) : null;
    const iconRoom =
      activityStatus?.isActive &&
      onoffStatus?.isOn &&
      userNearbyStatus?.nearRoom?.length > 0
        ? nearbyRoomWG(0)
        : null;
    return (
      <>
        {iconRoom}
        {iconEl}
        <MarkForWifi deviceId={deviceId} needToolTip={false} />
        <DeviceStatePower deviceId={deviceId} deviceType={DeviceType.vitals} />
      </>
    );
  }
);
VitalsIcons.displayName = "VitalsIcons";
export const MarkForBed = ({
  residentId,
  size,
}: {
  residentId: string;
  size?: number;
}): JSX.Element => {
  const { icon } = useMarkForBed({ residentId, size });
  if (typeof icon === "string") {
    if (icon === "") return null;
    return <i className={icon} />;
  }
  return icon;
};
MarkForBed.displayName = "MarkForBed";
export const MarkForResidentActivityStatus = React.memo(
  ({ devices }: { devices: DeviceMinimizedMap }): JSX.Element => {
    const selection = useMemo(() => residentActiveBasedonDevicesCreator(), []);
    const activeStatus = useSelector((state: IAppState) =>
      selection(state, Object.keys(devices))
    );
    if (Object.keys(devices).length === 0) return null;
    return (
      <div className="status-holder">
        {activeStatus ? (
          <i className="icon-status-online">
            <span className="path1"></span>
            <span className="path2"></span>
          </i>
        ) : (
          <i className="icon-status-offline">
            <span className="path1"></span>
            <span className="path2"></span>
          </i>
        )}
      </div>
    );
  }
);
MarkForResidentActivityStatus.displayName = "MarkForResidentActivityStatus";
export const MarkForPosture = React.memo(
  ({ biopatchId }: { biopatchId: string }): JSX.Element => {
    const { t } = useTranslation("status");
    const posture = useMemo(() => postrureStatusCreator(), []);
    const postureStatus = useSelector((state: IAppState) =>
      posture(state, biopatchId)
    );

    let labelTExt = "";
    let icon = "";

    switch (postureStatus?.value?.toLowerCase()) {
      case PostureStatusValue.BACK.toLowerCase(): {
        // if (status.value.toLowerCase() === BedStatusValue.IN_BED.toLowerCase()) {
        labelTExt = t("inBedBack");
        icon = "icon-lying-on-back";
        // }
        break;
      }
      case PostureStatusValue.LEFT.toLowerCase(): {
        // if (status.value.toLowerCase() === BedStatusValue.IN_BED.toLowerCase()) {
        labelTExt = t("inBedLeft");
        icon = "icon-lying-left";
        // }

        break;
      }
      case PostureStatusValue.RIGHT.toLowerCase(): {
        // if (status.value.toLowerCase() === BedStatusValue.IN_BED.toLowerCase()) {
        labelTExt = t("inBedRight");
        icon = "icon-lying-right";
        // }

        break;
      }
      case PostureStatusValue.STOMACH.toLowerCase(): {
        // if (status.value.toLowerCase() === BedStatusValue.IN_BED.toLowerCase()) {
        labelTExt = t("inBedBelly");
        icon = "icon-lying-on-belly";
        // }

        break;
      }
      case PostureStatusValue.STAND.toLowerCase(): {
        labelTExt = t("outOfBedStand");
        icon = "icon-standing";
        break;
      }
      case PostureStatusValue.SIT.toLowerCase(): {
        labelTExt = t("sitting");
        icon = "icon-sitting";
        break;
      }
      case PostureStatusValue.WALK.toLowerCase(): {
        labelTExt = t("walk");
        icon = "icon-walking";
        break;
      }
      case PostureStatusValue.FALL.toLowerCase(): {
        labelTExt = t("fall");
        icon = "icon-fall";
        break;
      }
      case PostureStatusValue.UNATTACHED.toLowerCase():
        labelTExt = t("notAttached");
        icon = "icon-not-attached";
        break;
    }
    return (
      <>
        {icon !== "" ? (
          <Tooltip title={labelTExt} key={labelTExt}>
            <i className={icon} />
          </Tooltip>
        ) : null}
      </>
    );
  }
);
MarkForPosture.displayName = "MarkForPosture";
export const MarkForVitalsBed = React.memo(
  ({
    vitalsId,
    residentId,
  }: {
    vitalsId: string;
    residentId: string;
  }): JSX.Element => {
    const { t } = useTranslation("common");
    const selection = useMemo(() => bedStatusCreator(), []);
    const bedStatus = useSelector((state: IAppState) =>
      selection(state, residentId)
    );

    const deviceOccupancy = useMemo(() => occupancyStatusCreator(), []);
    const occupancyStatus = useSelector((state: IAppState) =>
      deviceOccupancy(state, vitalsId)
    );
    const status = occupancyStatus
      ? { ...bedStatus, value: occupancyStatus.value }
      : bedStatus;
    const onoff = useMemo(() => onOffStatusCreator(), []);
    const onoffStatus = useSelector((state: IAppState) =>
      onoff(state, vitalsId)
    );
    const userNearby = useMemo(() => userNearbyStatusCreator(), []);
    const userNearbyStatus = useSelector((state: IAppState) =>
      userNearby(state, vitalsId)
    );
    if (!onoffStatus?.isOn) return <i className="icon-off" />;
    if (!status) return null;
    let labelTExt = "";
    let icon = "";
    if (!status || status?.value === BedStatusValue.INIT) return null;
    switch (status?.value?.toLowerCase()) {
      case BedStatusValue.IN_BED.toLowerCase():
        labelTExt = t("labels.inBed");
        icon = "icon-alert-bed-in";
        break;

      case BedStatusValue.OUT_OF_BED.toLowerCase():
        labelTExt = t("labels.outBed");
        icon = "icon-alert-bed-out";
        break;
    }

    return (
      <>
        {icon !== "" ? (
          <>
            <UserTagOrMobileDeviceAsTagGate>
              {userNearbyStatus?.nearRoom?.length > 0 && (
                <UserNeabyRoomWithTooltip userNearbyStatus={userNearbyStatus} />
              )}
            </UserTagOrMobileDeviceAsTagGate>

            {userNearbyStatus?.nearBed?.length > 0 ? (
              <UserNeabyBedWithTooltip
                icon={
                  icon === "icon-alert-bed-in" ? nearbyInGG() : nearbyOutGG()
                }
                userNearbyStatus={userNearbyStatus}
              />
            ) : (
              <Tooltip title={labelTExt} key={labelTExt}>
                <i className={icon} />
              </Tooltip>
            )}
          </>
        ) : null}
      </>
    );
  }
);
MarkForVitalsBed.displayName = "MarkForVitalsBed";
export const UserNeabyBedWithTooltip = React.memo(
  ({
    size,
    icon,
    userNearbyStatus,
  }: {
    size?: number;
    icon: any;
    userNearbyStatus: UserNearbyStatus;
  }): JSX.Element => {
    const { view, presentation } = useSelector(
      (state: IAppState) => state.alerts.settings
    );

    return userNearbyStatus?.nearBed?.length > 1 ? (
      <div
        className={`nearby-badge ${
          ViewType.Notification === 0 &&
          ViewType.Status === 1 &&
          PresentationType.Cards !== presentation &&
          PresentationType.Tile !== presentation
            ? "view-badge"
            : ""
        }`}
        style={{
          height:
            size && ViewType.Notification !== 0 && ViewType.Status !== 1
              ? "32px"
              : "",
          width:
            size && ViewType.Notification !== 0 && ViewType.Status !== 1
              ? "32px"
              : "",
        }}
      >
        <div
          className={`badge badge-small ${
            PresentationType.Tile === presentation ? "badge-tile" : ""
          }`}
        >
          {userNearbyStatus?.nearBed?.length}
        </div>
        {icon}
      </div>
    ) : (
      icon
    );
  }
);
UserNeabyBedWithTooltip.displayName = "UserNeabyBedWithTooltip";
const UserNeabyRoomWithTooltip = React.memo(
  ({
    userNearbyStatus,
    size,
  }: {
    userNearbyStatus: UserNearbyStatus;
    size?: number;
  }): JSX.Element => {
    return userNearbyStatus?.nearRoom?.length > 1 ? (
      <div className="nearby-badge">
        <div className="badge badge-small">
          {userNearbyStatus?.nearRoom?.length}
        </div>
        {nearbyRoomWG(size)}
      </div>
    ) : (
      nearbyRoomWG(size)
    );
  }
);
UserNeabyRoomWithTooltip.displayName = "UserNeabyRoomWithTooltip";
export const MarkForNearRoom = ({
  residentId,
  size,
}: {
  residentId: string;
  size?: number;
}): JSX.Element => {
  const ResidentSelection = useMemo(() => residentsCreator(), []);
  const resident = useSelector((state: IAppState) =>
    ResidentSelection(state, residentId)
  );
  if (!resident) {
    console.log("");
  }
  const vitalsId = Object.keys(resident.devicesMinimized).find(
    (el) => resident.devicesMinimized[el].type === DeviceType.vitals
  );

  const userNearby = useMemo(() => userNearbyStatusCreator(), []);
  const userNearbyStatus = useSelector((state: IAppState) =>
    userNearby(state, vitalsId)
  );

  if (userNearbyStatus?.nearRoom?.length)
    return (
      <UserNeabyRoomWithTooltip
        userNearbyStatus={userNearbyStatus}
        size={size}
      />
    );
  return null;
};
MarkForNearRoom.displayName = "MarkForNearRoom";
export const MarkForPresence = React.memo(
  ({ resident }: { resident: Resident }): JSX.Element => {
    return resident.nodePresence !== presenceStatuses.active ? (
      <div className="status-holder">
        <i
          className={`icon-status-${
            resident.nodePresence === presenceStatuses.discharged
              ? "discharged"
              : "away"
          }`}
        >
          <span className="path1"></span>
          <span className="path2"></span>
          <span className="path3"></span>
        </i>
      </div>
    ) : null;
  }
);
MarkForPresence.displayName = "MarkForPresence";
export const useMarkForBed = ({
  residentId,
  size,
}: {
  residentId: string;
  size?: number;
}): { label: string; icon: any } => {
  const { t } = useTranslation("common");
  const selection = useMemo(() => bedStatusCreator(), []);
  const bedStatus = useSelector((state: IAppState) =>
    selection(state, residentId)
  );

  const ResidentSelection = useMemo(() => residentsCreator(), []);
  const resident = useSelector((state: IAppState) =>
    ResidentSelection(state, residentId)
  );
  if (!resident) {
    console.log("");
  }
  let biopatchOrAnyId = Object.keys(resident.devicesMinimized).find(
    (el) => resident.devicesMinimized[el].type === DeviceType.biopatch
  );
  const vitalsId = Object.keys(resident.devicesMinimized).find(
    (el) => resident.devicesMinimized[el].type === DeviceType.vitals
  );

  if (!biopatchOrAnyId)
    biopatchOrAnyId = Object.keys(resident.devicesMinimized).find((el) => true);
  const posture = useMemo(() => postrureStatusCreator(), []);
  const postureStatus = useSelector((state: IAppState) =>
    posture(state, biopatchOrAnyId)
  );
  const bioActivity = useMemo(() => activityStatusCreator(), []);
  const bioActiveStatus = useSelector((state: IAppState) =>
    bioActivity(state, biopatchOrAnyId)
  );

  const vitalsActivity = useMemo(() => activityStatusCreator(), []);
  const vitalsActivityStatus = useSelector((state: IAppState) =>
    vitalsActivity(state, vitalsId)
  );

  const deviceOccupancy = useMemo(() => occupancyStatusCreator(), []);
  const occupancyStatus = useSelector((state: IAppState) =>
    deviceOccupancy(state, vitalsId)
  );
  const status = occupancyStatus
    ? { ...bedStatus, value: occupancyStatus.value }
    : bedStatus;

  const onoff = useMemo(() => onOffStatusCreator(), []);
  const onoffStatus = useSelector((state: IAppState) => onoff(state, vitalsId));
  const userNearby = useMemo(() => userNearbyStatusCreator(), []);
  const userNearbyStatus = useSelector((state: IAppState) =>
    userNearby(state, vitalsId)
  );

  if (vitalsId && biopatchOrAnyId === vitalsId && !onoffStatus?.isOn)
    return { label: t("labels.isOff"), icon: `icon-off font-${size}` };
  if (!status) return { label: "", icon: "" };
  let labelTExt = "";
  let icon = "";

  if (
    !bioActiveStatus ||
    !bioActiveStatus?.isActive ||
    !postureStatus ||
    postureStatus?.value === PostureStatusValue.UNKNOWN ||
    postureStatus?.value === PostureStatusValue.UNATTACHED
  ) {
    switch (status?.value?.toLowerCase()) {
      case BedStatusValue.IN_BED.toLowerCase():
        labelTExt = t("labels.inBed");
        icon = `icon-alert-bed-in font-${size}`;
        break;

      case BedStatusValue.INIT.toLowerCase():
      case BedStatusValue.OUT_OF_BED.toLowerCase():
        labelTExt = t("labels.outBed");
        icon = `icon-alert-bed-out font-${size} size-icon-${size / 2}`;
        break;
    }

    if (!vitalsActivityStatus || !vitalsActivityStatus?.isActive) {
      icon = "";
      labelTExt = "";
      return { label: "", icon: "" };
    }
    if (userNearbyStatus?.nearBed?.length > 0)
      return {
        icon: (
          <UserNeabyBedWithTooltip
            icon={
              icon === "icon-alert-bed-in font-16" || "icon-alert-bed-in"
                ? nearbyInWG(size)
                : nearbyOutWG(size)
            }
            userNearbyStatus={userNearbyStatus}
            size={size}
          />
        ),
        label: labelTExt,
      };
  } else
    switch (postureStatus?.value?.toLowerCase()) {
      case PostureStatusValue.BACK.toLowerCase(): {
        labelTExt = t("status:inBedBack");
        icon = "icon-lying-on-back font-16";
        if (userNearbyStatus?.nearBed?.length > 0)
          return { icon: nearbyBack(), label: labelTExt };
        break;
      }
      case PostureStatusValue.LEFT.toLowerCase(): {
        labelTExt = t("status:inBedLeft");
        icon = "icon-lying-left font-16";
        if (userNearbyStatus?.nearBed?.length > 0)
          return { icon: nearbyLeft(), label: labelTExt };
        break;
      }
      case PostureStatusValue.RIGHT.toLowerCase(): {
        labelTExt = t("status:inBedRight");
        icon = "icon-lying-right font-16";
        if (userNearbyStatus?.nearBed?.length > 0)
          return { icon: nearbyRight(size), label: labelTExt };
        break;
      }
      case PostureStatusValue.STOMACH.toLowerCase(): {
        labelTExt = t("status:inBedBelly");
        icon = "icon-lying-on-belly font-16";
        if (userNearbyStatus?.nearBed?.length > 0)
          return { icon: nearbyBelly(), label: labelTExt };
        break;
      }
      case PostureStatusValue.STAND.toLowerCase(): {
        labelTExt = t("status:outOfBedStand");
        icon = "icon-standing font-16";
        if (userNearbyStatus?.nearBed?.length > 0)
          return { icon: nearbyStand(), label: labelTExt };
        break;
      }
      case PostureStatusValue.SIT.toLowerCase():
        if (
          status.value.toLowerCase() === BedStatusValue.IN_BED.toLowerCase()
        ) {
          labelTExt = t("status:inBedSit");
          icon = "icon-bed-sitting-in font-16";
        } else {
          labelTExt = t("status:sitting");
          icon = "icon-sitting font-16";
        }
        if (userNearbyStatus?.nearBed?.length > 0)
          return { icon: nearbySit(), label: labelTExt };
        break;
      case PostureStatusValue.WALK.toLowerCase():
        labelTExt = t("status:walk");
        icon = "icon-walking font-16";
        break;
      case PostureStatusValue.FALL.toLowerCase():
        labelTExt = t("status:fall");
        icon = "icon-fall font-16";
        break;
      case PostureStatusValue.UNATTACHED.toLowerCase():
        labelTExt = t("status:notAttached");
        icon = "icon-not-attached font-16";
        break;
    }
  return { icon: `${icon} icon-big white`, label: labelTExt };
};

export const MarkForResidentAvatar = React.memo(
  ({ resident }: { resident: Resident }) => {
    const devicesSelection = useMemo(
      () => residentDevicesAfterGateCreator(),
      []
    );
    const residentDevices = useSelector((state: IAppState) =>
      devicesSelection(state, resident.id)
    );
    return resident.nodePresence === presenceStatuses.away ? (
      <div className="status-holder">
        <i className="icon-status-away">
          <span className="path1"></span>
          <span className="path2"></span>
          <span className="path3"></span>
        </i>
      </div>
    ) : (
      <MarkForResidentActivityStatus devices={residentDevices} />
    );
  }
);
MarkForResidentAvatar.displayName = "MarkForResidentAvatar";
